import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store/store'
import axios from 'axios'
import moment from 'moment'
import i18n from './locales'
import vuetify from './plugins/vuetify'
import {extend, localize, ValidationObserver, ValidationProvider} from 'vee-validate/dist/vee-validate.full'
import messagesDE from './locales/dictionary/validator_messages_de'
import lxvue from 'lxvue'
import lodash from 'lodash'
import LxBtn from './components/lx_button/LxBtn'

import lx_auth from './plugins/lx_auth'

import './components/lx_datepicker/LxDatepicker'
import './components/lx_search_field/LxSearchField'
import './components/lx_numberfield/LxNumberfield'
import './components/long_input/LongInput'

import '../node_modules/material-design-icons-iconfont/dist/material-design-icons.css'

import eventBus from './lib/eventBus'
import transport from './lib/transport'
import FormErrorHelper from './lib/formerrorhelper'

import './style/tumorscout-style.scss'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import '@mdi/font/css/materialdesignicons.css'

Vue.config.productionTip = false
moment.suppressDeprecationWarnings = true

/**
 *
 * Axios Settings
 *
 */

axios.defaults.baseURL = process.env.VUE_APP_BASE_API
axios.defaults.headers.common['client-id'] = process.env.VUE_APP_BASE_CLIENT_ID
axios.defaults.headers.common['frontend-version'] = process.env.PACKAGE_VERSION || '0.0.0';

const isDevBuild = process.env['NODE_ENV'] === 'development'

/**
 *
 * Use Components
 *
 */
Vue.use(transport, { propertyKey: '$http', forceBsonTransport: false, quiet: !isDevBuild })
Vue.use(lx_auth)

/**
 * Event Bus
 */
Vue.use(eventBus)

Vue.use(lxvue, {
  components: {
    vuetify
  }
})

Vue.use(FormErrorHelper, {
  components: {
    i18n
  }
})

/**
 *
 * Vee-Validate Settings:
 * - Lokalisierung auf Deutsch (de)
 * - Erweiterung der Regeln mittels extend (Passwort und Passwortwiederholung vergleichen)
 * - Komponenten registerieren ValidationProvider und ValidationObserver
 *
 */
localize('de', messagesDE)

extend('regex_validate', {
  validate: (value) => {
    try {
      new RegExp(value)
      return true
    } catch (e) {
      return false
    }
  },
  message: 'Ungültiges Regex'
})
extend('name_string', {
  message: 'Die Eingabe darf keine Zahlen, Sonderzeichen (außer [´\'’`,-.]) und keine Leerzeichen am Anfang und Ende enthalten.',
  validate: (value) => {
    let regex = RegExp(/^[A-Za-zÀ-ÖØ-öø-ž]+([´'’`,-.\s]+[A-Za-zÀ-ÖØ-öø-ž]+|[.]?)*$/g)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('address_string', {
  message: 'Die Eingabe darf keine Sonderzeichen (außer [,-.\'/+]()) und keine Leerzeichen am Anfang und Ende enthalten.',
  validate: (value) => {
    let regex = RegExp(/^[A-Za-zÀ-ÖØ-öø-ž0-9+]+([,-.'/\s]+[A-Za-zÀ-ÖØ-öø-ž0-9]+|[.]?|\s+[(]+[A-Za-zÀ-ÖØ-öø-ž0-9]+\s?[A-Za-zÀ-ÖØ-öø-ž0-9]+[)])*$/g)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('zip_string', {
  message: 'Die Postleitzahl darf nur aus Zahlen bestehen',
  validate: (value) => {
    let regex = RegExp(/^[0-9]*$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('trim_string', {
  message: 'Die Eingabe darf keine Leerzeichen am Anfang und Ende enthalten.',
  validate: (value) => {
    let regex = RegExp(/^\s+|\s+$/g)
    let valid = regex.test(value)

    return value ? !valid : true
  }
})
extend('password', {
  validate: (value, { other }) => value === other,
  message: '"Neues Passwort" und "Passwort wiederholen" stimmen nicht überein',
  params: [{ name: 'other', isTarget: true }]
})
extend('welcome_password', {
  validate: (value, { other }) => value === other,
  message: '"Passwort" und "Passwort wiederholen" stimmen nicht überein',
  params: [{ name: 'other', isTarget: true }]
})
extend('requireDependency', {
  params: [{ name: 'other', isTarget: true }],
  validate: (value, { other }) => {
    return !!other && !!value
  },
  message: '"Neues Passwort" und "Derzeitiges Passwort" werden benötigt'
})
extend('lanr_must_be_unique', {
  message: () => `Die eingegebene LANR existiert bereits.`,
  validate: (value, otherValue) => {
    function checkAvailability (physicians, indexOrigin) {
      return !(physicians.some(function (physician, index) {
        return physician.lanr === value && indexOrigin !== index
      }))
    }

    return value && Array.isArray(otherValue.array) && checkAvailability(otherValue.array, otherValue.index) && otherValue.lanr !== value
  },
  params: [{ name: 'array' }, { name: 'index' }, { name: 'lanr' }]
})
extend('either_lanr_or_iknr', {
  message: (field, params) => 'Der eingegebene Wert darf nicht parallel zur ' + params.contentLabel + ' eingegeben werden.',
  validate: (value, otherValue) => {
    return value ? value && !otherValue.content : true
  },
  params: [{ name: 'content' }, { name: 'contentLabel' }]
})
extend('icd10_code_auto_complete', {
  message: () => 'ICD Code wurde nicht erkannt. Bitte über Auswahlmöglichkeiten wählen.',
  validate: (value) => {
    let regex = RegExp(/^[CD]\d\d(\.\d(\d)?)?$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('icd10_code_OP_auto_complete', {
  message: () => 'ICD Code wurde nicht erkannt. Bitte über Auswahlmöglichkeiten wählen.',
  validate: (value, otherValue) => {
    let regex = RegExp(/^[E|G|I|K|M|R|T]\d\d(\.\d(\d)?)?$/)
    let valid = regex.test(value)

    return value ? !!(valid && otherValue.description) : true
  },
  params: [{ name: 'description' }]
})
extend('icd_o_code_auto_complete', {
  message: () => 'ICD-O Code wurde nicht erkannt. Bitte über Auswahlmöglichkeiten wählen.',
  validate: (value) => {
    let regex = RegExp(/^C\d\d(\.\d(\d)?)?$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('morphology_code_auto_complete', {
  message: () => 'Morphologie-Code bitte über Auswahlmöglichkeiten wählen. Der Code muss einem bestimmten Aufbau haben (z.B. 8140/3)',
  validate: (value) => {
    let regex = RegExp(/^\d\d\d\d\/\d$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('therapy_protocol_auto_complete', {
  message: () => 'Das Therapieprotokoll wurde nicht erkannt. Bitte über Auswahlmöglichkeiten wählen.',
  validate: (value, otherValue) => {
    return value ? !!otherValue.kindOfTherapy : true
  },
  params: [{ name: 'kindOfTherapy' }]
})
extend('date_not_in_future', {
  message: () => `Die Eingabe muss ein valides Datum sein und das Datum darf nicht in der Zukunft liegen.`,
  validate: (value) => {
    return moment(value, true).isValid() && (moment().isAfter(value, 'day') || moment().isSame(value, 'day'))
  }
})
extend('german_date', {
  message: () => `Die Eingabe ist kein gültiges Datum oder liegt vor dem 01.01.1900.`,
  validate: (value) => {
    // return valid, if no value
    if (value === null || value === undefined) return true

    // const current = new Date(typeof value === 'string' ? value : value.toISOString())
    const current = typeof value === 'string' ? value : value.toISOString()
    const currentMomentValue = moment(current)

    return moment(current, true).isValid() && currentMomentValue.isAfter('1899-12-31', 'day')
  }
})
extend('date_must_be_lesser_than_death', {
  message: (field, params) => 'Das Datum muss früher oder gleich dem Todestag (' + Vue.options.filters['formatDate'](params.deathDate, 'DD.MM.YYYY') + ') sein.',
  validate: (value, params) => {
    if (value && params.deathDate) {
      let accuracy = ''

      switch (params.dateAccuracy) {
        case 'E':   //  Exakt (entspricht taggenau)
          accuracy = 'day'
          break
        case 'T':   //  Tag geschätzt (entspricht monatsgenau)
          accuracy = 'month'
          break
        case 'M':    //  Monat geschätzt (entspricht jahrgenau)
          accuracy = 'year'
          break
        case 'V':   //  Vollständig geschätzt
          accuracy = 'year'
          break
        default:    // default ist Exakt (entspricht taggenau)
          accuracy = 'day'
      }
      return params.deathDate && moment(params.deathDate, true).isValid() ? moment(value).isBefore(params.deathDate, accuracy) || moment(value).isSame(params.deathDate, accuracy) : true
    } else {
      return true
    }
  },
  params: [{ name: 'deathDate' }, { name: 'dateAccuracy' }]
})
extend('death_date_must_be_latest_new_Import', {
  message: (value, params) => {
    let messageText = 'Es ' + (params.formsCount === 1 ? 'ist ' : 'sind ')
    messageText += params.formsCount + (params.formsCount === 1 ? ' Meldeanlass' : ' Meldeanlässe') + ' in der Historie'
    messageText += ' jünger als das eingegebene Sterbedatum. Bitte passen Sie diese an und versuchen es erneut.'

    return messageText
  },
  validate: (value, params) => {
    if (!value || !params.historyList || params.historyList.length === 0) {
      return true
    }

    // search for forms in history with date later than death date
    const formsWithLaterDate = (params.historyList || []).filter(form => (moment(form.date).isAfter(moment(value), 'day')))
    // set formsCount as param to work with to create error message
    params.formsCount = formsWithLaterDate.length

    return formsWithLaterDate.length === 0
  },
  params: [{ name: 'historyList' }, { name: 'formsCount' }]
})
extend('death_date_must_be_latest', {
  message: (deathValue, params) => {
    if (!params.documentation || !params.import) {
      // fallback message, if request to backend has empty result
      return 'Es ist ein Fehler bei der Validierung aufgetreten. Bitte versuchen Sie es erneut'
    } else if (params.isLongMessage) {
      // long message text with details in documentation
      let messageText = ''

      if (params.documentation.length > 0) {
        messageText += 'in der Patientendokumentation:' + '<br>'

        // forms in documentation
        params.documentation.forEach((form, index) => {
          // return date value in dependency to form
          function returnDateProperty () {
            let returnValue = ''

            switch (form.key) {
              case 'register':
                return 'Diagnosedatum: ' + Vue.options.filters['formatDate'](form.diagnosedatum, 'DD.MM.YYYY')
              case 'progress':
                return 'Untersuchungsdatum: ' + Vue.options.filters['formatDate'](form.untersuchungsdatum, 'DD.MM.YYYY')
              case 'operation':
                return 'OP-Datum: ' + Vue.options.filters['formatDate'](form.opDatum, 'DD.MM.YYYY')
              case 'radiationTherapy':
                if (form.stBeginnDatum) {
                  returnValue += 'Beginn-Datum: ' + Vue.options.filters['formatDate'](form.stBeginnDatum, 'DD.MM.YYYY')
                }
                if (form.stEndeDatum) {
                  returnValue += (form.stBeginnDatum ? ', ' : '') + 'Ende-Datum: ' + Vue.options.filters['formatDate'](form.stEndeDatum, 'DD.MM.YYYY')
                }
                return returnValue
              case 'systemicTherapy':
                if (form.systBeginnDatum) {
                  returnValue += 'Beginn-Datum: ' + Vue.options.filters['formatDate'](form.systBeginnDatum, 'DD.MM.YYYY')
                }
                if (form.systEndeDatum) {
                  returnValue += (form.systBeginnDatum ? ', ' : '') + 'Ende-Datum: ' + Vue.options.filters['formatDate'](form.systEndeDatum, 'DD.MM.YYYY')
                }
                return returnValue
              case 'tumorConference':
                return 'Tumorkonferenz-Datum: ' + Vue.options.filters['formatDate'](form.tumorkonferenzDatum, 'DD.MM.YYYY')
              default:
                return 'Datum unbekannt'
            }
          }

          if (index === params.documentation.length - 1) {
            messageText += '<span class="ml-5">' + i18n.t('forms.' + form.key) + ' ( Diagnose: ' + form.subjId + ', ' + returnDateProperty() + ' )</span><br>'
          } else {
            messageText += '<span class="ml-5">' + i18n.t('forms.' + form.key) + ' ( Diagnose: ' + form.subjId + ', ' + returnDateProperty() + ' ),</span><br>'
          }
        })
      }

      if (params.documentation.length > 0 && params.import.length > 0) {
        messageText += ' sowie '
      }

      // forms in import with later date
      if (params.import.length > 0) {
        messageText += 'im Import:' + '<br>'

        params.import.forEach((form, index) => {
          if (index === params.import.length - 1) {
            messageText += '<span class="ml-5">' + i18n.t('forms.' + form.formType) + ' ( Datum: ' + Vue.options.filters['formatDate'](form.date, 'DD.MM.YYYY') + ' )</span><br>'
          } else {
            messageText += '<span class="ml-5">' + i18n.t('forms.' + form.formType) + ' ( Datum: ' + Vue.options.filters['formatDate'](form.date, 'DD.MM.YYYY') + ' ),</span><br>'
          }
        })
      }

      return messageText
    } else {
      // short message text for import
      let messageText = 'Es sind Datumswerte '

      // add count of forms in documentation with later date to message, if exist
      if (params.documentation.length > 0) {
        messageText += 'in der Patientendokumentation(' + params.documentation.length + (params.documentation.length === 1 ? ' Formular' : ' Formulare') + ')'
      }

      if (params.documentation.length > 0 && params.import.length > 0) {
        messageText += ' sowie '
      }

      // add count of report reasons with later date in history to message, if exist
      if (params.import.length > 0) {
        messageText += 'im Import(' + params.import.length + (params.import.length === 1 ? ' Meldeanlass' : ' Meldeanlässe') + ')'
      }

      messageText += ' jünger als das eingegebene Sterbedatum. Bitte passen Sie diese an und versuchen es erneut.'

      return messageText
    }
  },
  validate: async (value, otherValues) => {
    const current = moment(value)

    // compare only if patientId and valid value exist
    if (!otherValues.patientId || !value || !current.isValid()) {
      return true
    }

    // build date value in format 'YYYYMMDD'
    const month = current.month() + 1 < 10 ? '0' + (current.month() + 1) : current.month() + 1
    const day = current.date() < 10 ? '0' + current.date() : current.date()
    const formattedDate = current.year().toString() + month.toString() + day.toString()

    const responseDeathDate = await axios.get('/documentation/checkDeathDate/' + otherValues.patientId + '/' + formattedDate)

    if (responseDeathDate.status === 200 && responseDeathDate.data) {
      // add documentation forms and import values to params to have access on them in message
      otherValues.documentation = responseDeathDate.data.forms || []
      otherValues.import = responseDeathDate.data.import || []

      return otherValues.documentation.length === 0 && otherValues.import.length === 0
    }
  },
  params: [{ name: 'patientId' }, { name: 'isLongMessage' }, { name: 'documentation' }, { name: 'import' }]
})
extend('date_must_be_lesser_than', {
  message: () => 'Das Datum muss kleiner/gleich dem Ende-Datum sein.',
  validate: (value, otherValue) => {
    const dateToCompare = otherValue.dateToCompare

    // return only validation error if compare-value exists
    return dateToCompare && moment(dateToCompare, true).isValid() ? moment(value).isBefore(dateToCompare, 'day') || moment(value).isSame(dateToCompare, 'day') : true
  },
  params: [{ name: 'dateToCompare' }]
})
extend('date_must_be_lesser_than_lead_date', {
  message: () => 'Das Datum muss kleiner/gleich dem Datum im Kopfbereich sein.',
  validate: (value, otherValue) => {
    const dateToCompare = otherValue.dateToCompare

    // return only validation error if compare-value exists
    return dateToCompare && moment(dateToCompare, true).isValid() ? moment(value).isBefore(dateToCompare, 'day') || moment(value).isSame(dateToCompare, 'day') : true
  },
  params: [{ name: 'dateToCompare' }]
})
extend('year_must_be_lesser_than_diagnosis', {
  message: () => 'Das Jahr muss früher oder gleich dem Jahr der Erstdiagnose sein.',
  validate: (value, otherValue) => {
    const valueDate = moment(value + '-01-01')
    const dateToCompare = otherValue.diagnosisDate

    // return only validation error if compare-value exists
    return dateToCompare && moment(dateToCompare, true).isValid() ? valueDate.isBefore(dateToCompare, 'year') || valueDate.isSame(dateToCompare, 'year') : true
  },
  params: [{ name: 'diagnosisDate' }]
})
extend('date_must_be_greater_than', {
  message: () => 'Das Datum muss größer sein als Datum Beginn.',
  validate: (value, otherValue) => {
    const dateToCompare = otherValue.dateToCompare

    // return only validation error if compare-value exists
    return dateToCompare && moment(dateToCompare, true).isValid() ? moment(value).isAfter(dateToCompare, 'day') || moment(value).isSame(dateToCompare, 'day') : true
  },
  params: [{ name: 'dateToCompare' }]
})
extend('date_must_be_greater_than_diagnosis', {
  message: () => 'Das Datum muss größer/gleich dem Datum der Erstdiagnose sein.',
  validate: (value, otherValue) => {
    const dateToCompare = otherValue.dateToCompare

    // return only validation error if compare-value exists
    return dateToCompare && moment(dateToCompare, true).isValid() ? moment(value).isAfter(dateToCompare, 'day') || moment(value).isSame(dateToCompare, 'day') : true
  },
  params: [{ name: 'dateToCompare' }]
})
extend('diag_date_must_be_oldest_date', {
  message: (field, params) => {
    if (!params.documentation || !params.import) {
      return 'Es ist ein Fehler bei der Validierung aufgetreten. Bitte versuchen Sie es erneut'
    } else {
      let messageText = 'Es sind Datumswerte '
      if (params.documentation.length > 0) {
        messageText += 'in der Patientendokumentation ('

        params.documentation.forEach((form, index) => {
          if (index === params.documentation.length - 1) {
            messageText += i18n.t('forms.' + form)
          } else {
            messageText = messageText + i18n.t('forms.' + form) + ', '
          }
        })

        messageText += ')'
      }

      if (params.documentation.length > 0 && params.import.length > 0) {
        messageText += ' sowie '
      }

      if (params.import.length > 0) {
        messageText += 'im Import ('

        params.import.forEach((form, index) => {
          if (index === params.import.length - 1) {
            messageText += i18n.t('forms.' + form)
          } else {
            messageText = messageText + i18n.t('forms.' + form) + ', '
          }
        })

        messageText += ')'
      }

      return messageText + ' älter als das eingegebene Diagnosedatum. Bitte passen Sie diese an und versuchen es erneut.'
    }
  },
  validate: async (value, otherValues) => {
    const current = moment(value)

    // compare only if formId exists (skip when new form)
    if (!otherValues.formId || !value || !current.isValid()) {
      return true
    }

    const response = await axios.get('/documentation/checkDiagnosisDate/' + otherValues.formId + '/' + current.toISOString())

    if (response.status === 200 && response.data) {
      otherValues.documentation = response.data.documentation || []
      otherValues.import = response.data.import || []

      return otherValues.documentation.length === 0 && otherValues.import.length === 0
    } else {
      return false
    }
  },
  params: [{ name: 'formId' }, { name: 'documentation' }, { name: 'import' }]
})
extend('ais_id_must_be_unique', {
  message: (field, params) => {
    if (params.error) {
      return 'Es ist ein Fehler bei der Prüfung der AIS-ID aufgetreten, versuchen Sie es bitte erneut.'
    }

    if (params.patient && params.patient.firstname && params.patient.lastname && params.patient.birthday) {
      let returnString = 'Ein Patient mit dieser AIS-ID existiert bereits ('
      returnString += params.patient.lastname + ', ' + params.patient.firstname + ' - geb. am ' + Vue.options.filters['formatDate'](params.patient.birthday, 'DD.MM.YYYY') + ').'

      return returnString
    }

    return 'Ein Patient mit dieser AIS-ID existiert bereits.'
  },
  validate: async (value, params) => {
    if (value) {
      params.patient = null
      params.error = false
      params.id = params.id || 'new'

      const response = await axios.get('/patients/checkAisIdExists/' + params.id + '/' + value)

      if (response.status === 200 && response.data) {
        if (response.data.error) {
          params.error = true
          return false
        }

        if (response.data.patient && response.data.exists) {
          params.patient = response.data.patient
          return false
        }
        return true
      }
    }
  },
  params: [{ name: 'id' }, { name: 'patient' }, { name: 'error' }]
})
extend('nodes_must_be_lesser_than', {
  message: () => 'Der Wert muss kleiner/gleich dem untersuchten Wert sein.',
  validate: (value, otherValue) => {
    const valueToCompare = otherValue.examinedNodeValue
    return valueToCompare ? value <= valueToCompare : true
  },
  params: [{ name: 'examinedNodeValue' }]
})
extend('nodes_must_be_greater_than', {
  message: () => 'Der Wert muss größer/gleich dem Wert befallener Knoten sein.',
  validate: (value, otherValue) => {
    const valueToCompare = otherValue.affectedNodeValue
    return valueToCompare ? value >= valueToCompare : true
  },
  params: [{ name: 'affectedNodeValue' }]
})
extend('stanza_must_be_lesser_than', {
  message: () => 'Der Wert muss kleiner/gleich der Anzahl Stanzen sein.',
  validate: (value, otherValue) => {
    const valueToCompare = otherValue.overallStanzaValue
    return valueToCompare ? value <= valueToCompare : true
  },
  params: [{ name: 'overallStanzaValue' }]
})
extend('stanza_must_be_greater_than', {
  message: () => 'Der Wert muss größer/gleich dem Wert positiver Stanzen sein.',
  validate: (value, otherValue) => {
    const valueToCompare = otherValue.positiveStanzaValue
    return valueToCompare ? value >= valueToCompare : true
  },
  params: [{ name: 'positiveStanzaValue' }]
})
extend('at_least_one_op_is_selected', {
  message: () => 'Es muss mindestens eine Operation ausgewählt sein.',
  validate: (value) => {
    return value && Array.isArray(value) && value.length > 0 && value.some(op => op.code)
  }
})
extend('at_least_one_agent_is_selected', {
  message: () => 'Es muss mindestens ein Wirkstoff ausgewählt und befüllt sein.',
  validate: (value, params) => {
    let agentsList = lodash.cloneDeep(value)
    agentsList = agentsList || []

    // agents-array is invalid if empty or the first and only element is an empty object
    const invalidAgentsArray = agentsList.length === 0 || (agentsList.length === 1 && !agentsList[0].bezeichnung)
    return !invalidAgentsArray && !params.noAgent && !params.passiveTherapy
  },
  params: [{ name: 'noAgent' }, { name: 'passiveTherapy' }]
})
extend('at_least_one_complication_is_selected', {
  message: () => 'Die Eingabe der Op-Komplikationen ist verpflichtend. Bitte wählen Sie aus der vorgegebenen Liste, fügen Sie Komplikationen über die Eingabe des ICD10-Codes hinzu oder geben SIe an, dass es keine Komplikationen gab. ',
  validate: (value) => {
    return !!(value && (value.komplikation_nein_oder_unbekannt || Object.keys(value.kuerzel).length > 0 || (value.icd || []).length > 0))
  },
  params: [{ name: 'noComplication' }, { name: 'kuerzel' }, { name: 'icd' }]
})
extend('side_effect_must_be_filled', {
  message: () => 'Der Bereich der Nebenwirkungen muss ausgefüllt sein. Wenn zu Behandlungsbeginn keine Nebenwirkungen bekannt sind, dann ist die Ausprägung "Unbekannt" zu wählen.',
  validate: (value, params) => {
    if (params.required) {
      return !!(value.grad_maximal_2_oder_unbekannt || (value.nebenwirkungen || []).length > 0)
    } else {
      return true
    }
  },
  params: [{ name: 'required' }, { name: 'grad_maximal_2_oder_unbekannt' }, { name: 'nebenwirkungen' }]
})
extend('at_least_one_kind_of_therapy_is_selected', {
  message: () => 'Es muss mindestens eine Therapieart ausgewählt sein.',
  validate: (value) => {
    if (!value || typeof value !== 'object' || (typeof value === 'object' && Object.keys(value).length === 0)) {
      return false
    } else {
      // return true if at least one element is true
      return Object.keys(value).some(key => value[key])
    }
  }
})
extend('at_least_one_therapy_recommendation_is_selected', {
  message: () => 'Es muss mindestens eine Therapieempfehlung ausgewählt sein.',
  validate: (value) => {
    return value && Array.isArray(value) && value.length > 0
  }
})
extend('agent_must_be_unique', {
  message: () => 'Der Wirkstoff darf nur einmal in der Liste existieren',
  validate: (value, params) => {
    return (params.agentsList || []).filter((agent => agent && agent.bezeichnung === value)).length < 2
  },
  params: [{ name: 'agentsList' }]
})
extend('at_least_one_tnm_is_selected', {
  message: () => 'Es muss mindestens ein TNM-Wert (T, N oder M) ausgewählt sein.',
  validate: (value, params) => {
    return params.isRequired ? !!(params.tValue || params.nValue || params.mValue) : true
  },
  params: [{ name: 'isRequired' }, { name: 'tValue' }, { name: 'nValue' }, { name: 'mValue' }, { name: 'path' }]
})
extend('pT1c_excluded', {
  message: () => 'Die Ausprägung pT1c gibt es nicht bei einer C61-Diagnose.',
  validate: (value, otherValues) => {
    return otherValues.diagCode === 'C61' ? otherValues.tnmValueText !== 'pT1c' : true
  },
  params: [{ name: 'diagCode' }, { name: 'tnmValueText' }]
})
extend('at_least_one_performance_state_is_selected', {
  message: () => 'Fehler',
  validate: (value, params) => {
    return Boolean(params.ecog || params.karnofsky || params.nd)
  },
  params: [{ name: 'ecog' }, { name: 'karnofsky' }, { name: 'nd' }]
})
extend('tnm_date_must_be_empty', {
  message: () => 'Das Datum des Stagings darf nicht alleine stehen. Bitte füllen Sie den TNM aus oder löschen Sie das Datum und wenn vorhanden die y/r/a-Klassifikation.',
  validate: (value, params) => {
    return !params.isRequired ? !!(params.base.tnm_t || params.base.tnm_m || params.base.tnm_n) : true
  },
  params: [{ name: 'isRequired' }, { name: 'base' }]
})
extend('single_dosage_must_be_lesser_than_overall_dosage', {
  message: () => 'Der Wert muss kleiner/gleich der Gesamtdosis sein.',
  validate: (value, otherValue) => {
    const valueToCompare = otherValue.overallDosageValue
    return valueToCompare ? value <= valueToCompare : true
  },
  params: [{ name: 'overallDosageValue' }]
})
extend('overall_dosage_must_be_greater_than_single_dosage', {
  message: () => 'Der Wert muss größer/gleich der Einzeldosis sein.',
  validate: (value, otherValue) => {
    const valueToCompare = otherValue.singleDosageValue
    return valueToCompare ? value >= valueToCompare : true
  },
  params: [{ name: 'singleDosageValue' }]
})
extend('decimal', {
  message: (field, n) => {
    void n === 0 && (n = [])
    let t = n.decimals || 'keine'
    return void t === 0 && (t = '*'), 'Der Wert muss numerisch sein und darf ' + (t === '*' ? '' : t) + ' Nachkommastellen enthalten.'
  },
  validate: (value, params) => {
    function isNullOrUndefined () {
      let values = [], len = arguments.length
      while (len--) values[len] = arguments[len]

      return values.every(function (value) {
        return value === null || value === undefined
      })
    }

    if (params === void 0) params = {}

    let decimals = params.decimals
    if (decimals === void 0) decimals = '*'

    let separator = params.separator
    if (separator === void 0) separator = '.'

    if (isNullOrUndefined(value) || value === '') {
      return false
    }

    // if is 0.
    if (Number(decimals) === 0) return /^-?\d*$/.test(value)

    const regexPart = decimals === '*' ? '+' : ("{1," + decimals + "}")
    const regex = new RegExp(("^[-+]?\\d*(\\" + separator + "\\d" + regexPart + ")?([eE]{1}[-]?\\d+)?$"))

    return regex.test(value)
  },
  params: [{ name: 'decimals' }, { name: 'separator' }]
})
extend('reporter_is_required', {
  message: () => `Dieses Feld ist ein Pflichtfeld`,
  validate: (value) => {
    return !!(value && value.name)
  }
})
extend('lanr', {
  message: () => 'Bitte geben Sie eine gültige LANR ein.',
  validate: (value) => {
    if (!value || isNaN(value) || value.toString().length !== 9) {
      return false
    }

    const lanr = value.toString()
    let sum = 0

    for (let i = 0; i < 6; i++) {
      sum += lanr[i] * (i % 2 === 0 ? 4 : 9)
    }

    const mod = (sum % 10)
    let result = 0

    if (mod > 0) {
      result = 10 - mod
    }
    return result === parseInt(lanr[6])
  }
})
extend('iban', {
  message: () => 'Die IBAN ist ungültig',
  validate: (value) => {
    const CODE_LENGTHS = {
      AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
      CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
      FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
      HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
      LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
      MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
      RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26
    }
    // keep only alphanumeric characters
    const iban = String(value).toUpperCase().replace(/[^A-Z0-9]/g, '')
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/)
    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
      return false
    }

    // rearrange country code and check digits, and convert chars to ints
    const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter) => {
      return letter.charCodeAt(0) - 55
    });

    // calculate checksum
    let checksum = digits.slice(0, 2)
    for (let offset = 2; offset < digits.length; offset += 7) {
      const fragment = checksum + digits.substring(offset, offset + 7)
      checksum = (parseInt(fragment, 10) % 97).toString()
    }
    // returns true if the IBAN is valid
    return checksum === '1'
  }
})
extend('unique_login_name', {
  validate: async (value, params) => {
    if (value && params && params.token && params.email && value !== params.email) {
      const response = await axios.post('/checkUsernameExists', { token: params.token, username: value })

      if (response.status === 200 && typeof response.data.exists === 'boolean') {
        return !response.data.exists
      }
    }
    return true
  },
  message: i18n.t('AUTH.WELCOME.ERRORS.USERNAME_ALREADY_EXISTS'),
  params: [{ name: 'token' }, { name: 'email' }]
})
extend('tumor_state_overall', {
  message: () => 'Es muss mindestens einer der nachfolgenden Tumorstatus ausgefüllt sein',
  validate: (value, params) => {
    const matchDiagnosisRegex = RegExp(/^C61|C8|C9/)
    const isDiagnosisWithoutSuBStatus =  matchDiagnosisRegex.test(params.diagCode)

    if (value && ['P', 'Y', 'D', 'B', 'R'].indexOf(value) > -1 && !params.local && !params.lymph && !params.metastases && !isDiagnosisWithoutSuBStatus) {
      return false
    } else return !(value && value === 'T' && !params.local && !params.lymph && !params.metastases && !isDiagnosisWithoutSuBStatus)
  },
  params: [{ name: 'local' }, { name: 'lymph' }, { name: 'metastases' }, { name: 'diagCode' }]
})
extend('tmn_value_required', {
  message: (field, params) => {
    return 'Bei gesetztem Präfix muss der ' + params.valueName + ' gesetzt werden.'
  },
  validate: (value, params) => {
    return (!params.tnmValue && !value) || !!(params.tnmValue && value)
  },
  params: [{ name: 'tnmValue' }, { name: 'valueName' }]
})
extend('insurance_number', {
  message: () => 'Die Versichertennummer entspricht nicht dem Aufbau einer Versichertennummer der gesetzlichen Krankenversicherung',
  validate: (value) => {
    let regex = RegExp(/^[A-Z]\d{9}$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('insurance_company_gkv', {
  message: () => 'Das Institutionskennzeichen entspricht nicht dem Aufbau einer gesetzlichen Krankenversicherung',
  validate: (value) => {
    let regex = RegExp(/^10\d{7}$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('insurance_company_pkv', {
  message: () => 'Das Institutionskennzeichen entspricht nicht dem Aufbau einer privaten Krankenversicherung',
  validate: (value) => {
    let regex = RegExp(/^16\d{7}|950\d{6}$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})
extend('insurance_company_sonstige', {
  message: () => 'Das Institutionskennzeichen muss neunstellig sein und darf nur aus Zahlen bestehen',
  validate: (value) => {
    let regex = RegExp(/\d{9}$/)
    let valid = regex.test(value)

    return value ? valid : true
  }
})

Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)

Vue.filter('formatDate', function (value, _format) {
  let format = _format || 'DD.MM.YYYY'
  moment.locale('de')
  if (value) {
    const dateValue = typeof value === 'string' ? value : value.toISOString()
    return moment(new Date(dateValue)).format(format)
  } else {
    return null
  }
})

Vue.filter('currency', function (amount) {
  let value = parseFloat(amount)
  return value.toFixed(2) + ' €'
})

/**
 *
 * Eigener Filter, damit man mit einer Pipe ein Datum im HTML in die form DD.MM.YYYY bringen kann
 *
 * */
Vue.filter('leadingZero', function (value, size) {
  let s = value.toString()
  while (s.length < (size || 2)) {
    s = '0' + s
  }
  return s
})

Vue.filter('pretty', function (value, size) {
  if (value === null || value === undefined) {
    return 'no data'
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.join('\n')
    }

    return JSON.stringify(value, null, size || 4)
  }

  return value
})


String.prototype.hashCode = function () {
  let hash = 0, i, chr
  if (this.length === 0) return hash

  for (i = 0; i < this.length; i++) {
    chr = this.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr
    hash |= 0; // Convert to 32bit integer
  }
  return (hash >>> 0)
}

window.history.pushState(null, null, window.location.href)
window.onpopstate = function () {
  window.history.go(1)
}

/**
 *
 * Vue Instance
 *
 */
new Vue({
  i18n,
  router,
  store,
  vuetify,
  LxBtn,
  render: h => h(App)
}).$mount('#app')
